import { Quote, Star } from "lucide-react";
import { SectionHeader } from "./EditorialDepartments";

// Hand-picked placeholder reviews. Replace via admin panel when the live
// reviews API is wired to this section.
const FEATURED = {
    name: "Anjali K.",
    city: "Delhi",
    rating: 5,
    text:
        "Sunali personally followed up after delivery — that level of care is rare. " +
        "The fabric of the anarkali was even richer than the photos suggested. " +
        "I've already ordered two more pieces for the wedding season.",
    piece: "Wine velvet anarkali",
};

const SUPPORTING = [
    {
        name: "Priya M.",
        city: "Mumbai",
        rating: 5,
        text: "The fit was perfect — and the cord set held its shape after three washes. Will be back!",
    },
    {
        name: "Riya S.",
        city: "Bengaluru",
        rating: 5,
        text: "Got compliments all evening at the wedding. Loved how quickly it arrived.",
    },
];

/**
 * Editorial layout: one large featured review on the left, two supporting
 * cards stacked on the right. Soft ivory cards on an ivory background —
 * separation comes from typography and the gold star rating, not chrome.
 */
export default function EditorialTestimonials() {
    return (
        <section
            data-testid="shop-testimonials"
            className="mb-12 lg:mb-28 animate-fade-up"
        >
            <SectionHeader
                overline="Customer love"
                title={<>The boutique <span className="italic text-wine">experience</span>, in their words.</>}
                subtitle="Real reviews from real women across India — drop us a note after your order arrives and yours might land here next."
            />

            <div className="mt-10 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-4 lg:gap-6">
                {/* Featured */}
                <FeaturedReview r={FEATURED} />

                {/* Supporting stack */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4 lg:gap-6">
                    {/* Phase 2 fold-budget: mobile shows featured + 1 supporting
                        review; the rest join from sm: up. */}
                    {SUPPORTING.map((r, i) => (
                        <div key={i} className={i > 0 ? "hidden sm:block" : ""}>
                            <CompactReview r={r} idx={i} />
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function FeaturedReview({ r }) {
    return (
        <figure
            data-testid="testimonial-featured"
            className="relative rounded-3xl bg-ivory-soft border border-rosegold/15 p-8 lg:p-12 flex flex-col"
        >
            <Quote size={36} className="text-rosegold/30 mb-5" strokeWidth={1.2} />

            <div className="flex items-center gap-1 mb-4">
                {Array.from({ length: r.rating }).map((_, i) => (
                    <Star key={i} size={14} className="text-rosegold fill-rosegold" />
                ))}
            </div>

            <blockquote className="font-display text-2xl lg:text-3xl leading-[1.25] text-ink mb-6 font-light">
                "{r.text}"
            </blockquote>

            <figcaption className="mt-auto pt-6 border-t border-rosegold/15 flex items-center justify-between gap-3">
                <div>
                    <p className="font-display text-lg text-ink leading-none">{r.name}</p>
                    <p className="text-[11px] uppercase tracking-[0.18em] text-ink-soft mt-1">{r.city}</p>
                </div>
                {r.piece && (
                    <p className="text-[10px] uppercase tracking-[0.18em] text-rosegold font-bold">
                        on the {r.piece}
                    </p>
                )}
            </figcaption>
        </figure>
    );
}

function CompactReview({ r, idx }) {
    return (
        <figure
            data-testid={`testimonial-${idx}`}
            className="rounded-3xl bg-white border border-stone-200/80 p-6 lg:p-7 flex flex-col h-full"
        >
            <div className="flex items-center gap-1 mb-3">
                {Array.from({ length: r.rating }).map((_, i) => (
                    <Star key={i} size={12} className="text-rosegold fill-rosegold" />
                ))}
            </div>
            <blockquote className="text-[14px] text-ink leading-relaxed flex-1 mb-4">
                "{r.text}"
            </blockquote>
            <figcaption className="pt-4 border-t border-stone-200/80">
                <p className="font-display text-base text-ink leading-none">{r.name}</p>
                <p className="text-[10px] uppercase tracking-[0.18em] text-ink-soft mt-1">{r.city}</p>
            </figcaption>
        </figure>
    );
}
